import { Patient } from './PatientTypes';
import type { UserUuid } from '../identity';

export interface TreatmentPlanSectionInfo {
  section_key: string;
  name: string;
  color: string;
  sort_order?: number;
}

export type StageType =
  | 'trigger'
  | 'action'
  | 'condition'
  | 'time_condition'
  | 'watcher'
  | 'manual'
  | 'sub_plan_selection'
  | 'appointment'
  | 'practitioner_task'
  | 'patient_analysis'
  | 'practitioner_option'
  | 'comprehensive_analysis'
  | 'admin_review';

export type PractitionerTaskType =
  | 'upload_resource'
  | 'complete_practitioner_form'
  | 'create_test_order'
  | 'document_medical_history'
  | 'record_vital_signs'
  | 'complete_referral'
  | 'prescribe_medication'
  | 'select_dispensing_pharmacy';

export interface PractitionerTaskConfig {
  task_type: PractitionerTaskType;
  task_title?: string;
  task_instructions?: string;
  task_config: Record<string, any>;
}

export type OptionConnectionType = `option_${string}`;
export type ConnectionType = 'default' | 'condition_yes' | 'condition_no' | 'sub_plan' | OptionConnectionType;

export interface PractitionerOption {
  option_key: string;
  label: string;
  description?: string;
}
export type PlanStatus = 'active' | 'paused' | 'completed' | 'cancelled';
export type StageLogStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped' | 'waiting';

export interface TreatmentPlanStage {
  id: number;
  template_id: number;
  stage_key: string;
  stage_type: StageType;
  label: string;
  description?: string;
  configuration: Record<string, any>;
  position_x: number;
  position_y: number;
  progression_type: string;
  is_entry_point: boolean;
  parent_stage_id?: number;
  created_at: string;
  updated_at: string;
  connections?: TreatmentPlanStageConnection[];
}

export interface TreatmentPlanStageConnection {
  id: number;
  template_id: number;
  from_stage_id: number;
  to_stage_id: number;
  connection_type: ConnectionType;
  label?: string;
}

export interface TreatmentPlanSubPlan {
  id: number;
  template_id: number;
  parent_stage_id: number;
  entry_stage_id: number;
  label: string;
  description?: string;
  order: number;
}

// ── Sub-plan option stored in a sub_plan_selection node's configuration ──
export interface SubPlanOption {
  template_id: number;
  template_name: string;
}

export type SubPlanSelectionMode = 'auto_all' | 'practitioner_select';

export interface TreatmentPlanTemplate {
  id: number;
  company: string;
  name: string;
  description?: string;
  category?: string;
  is_active: boolean;
  version: number;
  parent_template_id?: number;
  canvas_data?: CanvasData;
  deleted_at?: string;
  created_at: string;
  updated_at: string;
  stages?: TreatmentPlanStage[];
  connections?: TreatmentPlanStageConnection[];
  sub_plans?: TreatmentPlanSubPlan[];
  is_peptide_plan?: boolean;
  is_global?: boolean;
}

export interface CanvasData {
  nodes: FlowNode[];
  edges: FlowEdge[];
  viewport?: { x: number; y: number; zoom: number };
}

export interface FlowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: FlowNodeData;
}

export interface FlowNodeData {
  stage_key: string;
  label: string;
  description?: string;
  stage_type: StageType;
  configuration: Record<string, any>;
  is_entry_point: boolean;
  is_required?: boolean;
  /** Logical stage group number computed from DAG depth (BFS level from entry points). */
  stage_number?: number;
  is_loop_target?: boolean;
  [key: string]: any;
}

export function detectBackEdges(nodes: FlowNode[], edges: FlowEdge[]): Set<string> {
  const adj = new Map<string, { edgeId: string; target: string }[]>();
  nodes.forEach((n) => adj.set(n.id, []));
  edges.forEach((e) => {
    adj.get(e.source)?.push({ edgeId: e.id, target: e.target });
  });

  const visited = new Set<string>();
  const inStack = new Set<string>();
  const backEdgeIds = new Set<string>();

  const dfs = (nodeId: string) => {
    visited.add(nodeId);
    inStack.add(nodeId);
    for (const { edgeId, target } of adj.get(nodeId) ?? []) {
      if (!visited.has(target)) {
        dfs(target);
      } else if (inStack.has(target)) {
        backEdgeIds.add(edgeId);
      }
    }
    inStack.delete(nodeId);
  };

  const inDeg = new Map<string, number>();
  nodes.forEach((n) => inDeg.set(n.id, 0));
  edges.forEach((e) => inDeg.set(e.target, (inDeg.get(e.target) ?? 0) + 1));
  nodes.filter((n) => (inDeg.get(n.id) ?? 0) === 0).forEach((n) => {
    if (!visited.has(n.id)) dfs(n.id);
  });
  nodes.forEach((n) => { if (!visited.has(n.id)) dfs(n.id); });

  return backEdgeIds;
}

/**
 * Compute a logical stage_number (BFS depth) for every node in the canvas DAG.
 * Nodes at the same depth share the same stage_number — This means condition
 * Yes/No branches always get the same number, not separate sequential stages.
 *
 * Algorithm: topological sort → propagate max(predecessor depth) + 1.
 * Entry-point / root nodes start at 1.
 */
export function computeStageNumbers(
  nodes: FlowNode[],
  edges: FlowEdge[],
  backEdgeIds: Set<string> = new Set(),
): Map<string, number> {
  const forwardEdges = backEdgeIds.size > 0 ? edges.filter((e) => !backEdgeIds.has(e.id)) : edges;

  // Build forward adjacency + in-degree
  const adj = new Map<string, string[]>();
  const deg = new Map<string, number>();
  nodes.forEach(n => { adj.set(n.id, []); deg.set(n.id, 0); });
  forwardEdges.forEach(e => {
    adj.get(e.source)?.push(e.target);
    deg.set(e.target, (deg.get(e.target) ?? 0) + 1);
  });

  // Kahn's topological sort
  const queue = nodes.filter(n => (deg.get(n.id) ?? 0) === 0).map(n => n.id);
  const topo: string[] = [];
  const degCopy = new Map(deg);
  while (queue.length) {
    const id = queue.shift()!;
    topo.push(id);
    adj.get(id)?.forEach(t => {
      const d = (degCopy.get(t) ?? 0) - 1;
      degCopy.set(t, d);
      if (d === 0) queue.push(t);
    });
  }
  // Handle any remaining nodes (cycles / disconnected)
  const topoSet = new Set(topo);
  nodes.forEach(n => { if (!topoSet.has(n.id)) topo.push(n.id); });

  // Propagate levels in topological order
  const levels = new Map<string, number>();
  topo.forEach(id => {
    const parentLevels = forwardEdges
      .filter(e => e.target === id)
      .map(e => levels.get(e.source) ?? 0);
    levels.set(id, parentLevels.length === 0 ? 1 : Math.max(...parentLevels) + 1);
  });

  return levels;
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  label?: string;
  type?: string;
  animated?: boolean;
  data?: {
    connection_type: ConnectionType;
    is_back_edge?: boolean;
  };
}

/** Frozen snapshot of a TreatmentPlanStage captured at plan assignment time. */
export interface PatientPlanStageSnapshot {
  id: number;
  patient_treatment_plan_id: number;
  original_stage_id?: number;
  stage_key: string;
  stage_order: number;
  title?: string;
  description?: string;
  stage_type: StageType;
  configuration?: Record<string, any>;
  progression_type: string;
  is_entry_point: boolean;
  is_required?: boolean;
  metadata?: Record<string, any>;
  section_key?: string | null;
  /** Loop iteration this snapshot belongs to (0-based). Optional until backend B3 ships. */
  loop_iteration?: number;
  created_at: string;
  updated_at: string;
}

export interface PatientTreatmentPlan {
  id: number;
  patient_id: UserUuid;
  company: string;
  template_id: number;
  treatment_plan_template_id?: number;
  template?: TreatmentPlanTemplate;
  status: PlanStatus;
  current_stage_id?: number;
  current_stage?: TreatmentPlanStage;
  current_stage_snapshot_id?: number;
  current_stage_snapshot?: PatientPlanStageSnapshot;
  started_at?: string;
  assigned_at?: string;
  completed_at?: string;
  paused_at?: string;
  cancelled_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  stage_logs?: PatientTreatmentPlanStageLog[];
  current_section?: TreatmentPlanSectionInfo | null;
  sections?: TreatmentPlanSectionInfo[];
	patient: Patient
}

// ── Watcher types ───────────────────────────────────────────────────────────

export interface WatcherItem {
  item_key: string;
  label: string;
  watcher_type: string;
  watch_config: Record<string, any>;
  is_triggered: boolean;
  triggered_at?: string | null;
}

export interface TreatmentPlanWatcher {
  id: number;
  patient_treatment_plan_id: number;
  treatment_plan_stage_id: number;
  watcher_type?: string | null;
  watch_config?: Record<string, any> | null;
  watcher_items?: WatcherItem[] | null;
  is_active: boolean;
  triggered_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PatientTreatmentPlanStageLog {
  id: number;
  patient_treatment_plan_id: number;
  patient_plan_id: number;
  treatment_plan_stage_id?: number;
  stage_snapshot_id?: number;
  /** Live stage from the template — may reflect template edits; prefer stage_snapshot. */
  stage?: TreatmentPlanStage;
  /** Frozen stage definition captured at plan assignment time. Use this for display. */
  stage_snapshot?: PatientPlanStageSnapshot;
  status: StageLogStatus;
  entered_at?: string;
  started_at?: string;
  completed_at?: string;
  outcome?: string;
  outcome_data?: Record<string, any>;
  actions_performed?: Record<string, any>[];
  error_message?: string;
  watcher?: TreatmentPlanWatcher | null;
  /** Loop iteration this log belongs to (0-based). Optional until backend B3 ships. */
  loop_iteration?: number;
}

export interface TreatmentNoteTemplate {
  id: number;
  company: string;
  name: string;
  description?: string;
  category?: string;
  is_active: boolean;
  structure: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface TreatmentNote {
  id: number;
  patient_id: UserUuid;
  patient_treatment_plan_id?: number;
  treatment_note_template_id?: number;
  appointment_id?: number;
  practitioner_id?: UserUuid;
  company: string;
  title?: string;
  responses: Record<string, any>;
  status: 'draft' | 'completed';
  completed_at?: string;
  created_at: string;
  updated_at: string;
  note_template?: TreatmentNoteTemplate;
}

// ── Builder metadata ────────────────────────────────────────────────────

export const STAGE_CONFIG: Record<StageType, { label: string; color: string; description: string }> = {
  trigger: { label: 'Trigger', color: '#10b981', description: 'Entry point – how the plan is initiated' },
  action: { label: 'Action', color: '#3b82f6', description: 'Execute one or more actions' },
  condition: { label: 'Condition', color: '#f59e0b', description: 'Branch based on a condition (Yes / No)' },
  time_condition: { label: 'Time Delay', color: '#8b5cf6', description: 'Wait for a time-based condition' },
  watcher: { label: 'Watcher', color: '#06b6d4', description: 'Wait for an external event' },
  manual: { label: 'Manual Step', color: '#6b7280', description: 'Requires practitioner action' },
  sub_plan_selection: { label: 'Sub-Plan', color: '#ec4899', description: 'Branch into sub-plans' },
  appointment: { label: 'Appointment', color: '#f97316', description: 'Appointment-related step' },
  practitioner_task: { label: 'Practitioner Task', color: '#7c3aed', description: 'Practitioner must complete a clinical task before the plan continues' },
  patient_analysis: { label: 'Patient Analysis', color: '#0d9488', description: 'Auto-generates AI consult guidance from patient profile. Pauses until practitioner reviews.' },
  practitioner_option: { label: 'Decision Point', color: '#dc2626', description: 'Practitioner selects one path from multiple options to continue the plan' },
  comprehensive_analysis: { label: 'Comprehensive Analysis', color: '#0E4D6E', description: 'Cross-references genetic panel, medical history, labs, medications, diet and exercise data. Requires practitioner review.' },
  admin_review: { label: 'Admin Review', color: '#dc2626', description: 'Plan frozen — awaiting Xoe Med admin review' },
};

export const PRACTITIONER_TASK_TYPES: { value: PractitionerTaskType; label: string }[] = [
  { value: 'upload_resource',              label: 'Upload Document / Resource' },
  { value: 'complete_practitioner_form',   label: 'Complete Practitioner Form' },
  { value: 'create_test_order',            label: 'Create Test Order' },
  { value: 'document_medical_history',     label: 'Document Medical History' },
  { value: 'record_vital_signs',           label: 'Record Vital Signs' },
  { value: 'complete_referral',            label: 'Complete Referral' },
  { value: 'prescribe_medication',         label: 'Prescribe Medication' },
  { value: 'select_dispensing_pharmacy',   label: 'Select Dispensing Pharmacy (Pathway A)' },
];

export const ACTION_TYPES = [
  { value: 'send_notification',     label: 'Send Notification' },
  { value: 'assign_form',           label: 'Assign Form' },
  { value: 'assign_medication',     label: 'Assign Medication' },
  { value: 'cancel_appointment',    label: 'Cancel Appointment' },
  { value: 'dispatch_consult_job',  label: 'Generate Consultation Guidance' },
  { value: 'assign_global_resource', label: 'Assign Global Resource' },
  { value: 'request_script_upload', label: 'Request Script Upload' },
  { value: 'send_booking_link',     label: 'Send Booking Link' },
  // { value: 'create_test_order', label: 'Create Test Order' },
];

export const TRIGGER_EVENTS = [
  { value: 'form_completed', label: 'Form Completed' },
  { value: 'appointment_created', label: 'Appointment Created' },
  { value: 'plan_assigned', label: 'Plan Assigned' },
	// { value: 'blood_test_results_uploaded', label: 'Blood Test Results Available' },
  // { value: 'medication_script_uploaded', label: 'Script Uploaded' },
];

export const WATCHER_TYPES = [
  { value: 'form_completed', label: 'Form Completed' },
  { value: 'appointment_booked', label: 'Appointment Booked' },
  { value: 'appointment_status', label: 'Appointment Status Change' },
  // { value: 'test_results_available', label: 'Test Results Available' },
  { value: 'medication_script_uploaded', label: 'Script Uploaded' },
];

export const TIME_ANCHORS = [
  { value: 'stage_entry', label: 'Stage Entry' },
  { value: 'plan_start', label: 'Plan Start' },
  { value: 'next_appointment', label: 'Next Appointment' },
];

export const PLAN_STATUS_COLORS: Record<PlanStatus, string> = {
  active: 'green',
  paused: 'orange',
  completed: 'blue',
  cancelled: 'red',
};

export const STAGE_LOG_STATUS_COLORS: Record<StageLogStatus, string> = {
  pending: 'default',
  in_progress: 'processing',
  completed: 'success',
  failed: 'error',
  skipped: 'default',
  waiting: 'warning',
};

export const CONDITION_TYPES = [
  { value: 'form_completed', label: 'Form Completed' },
  { value: 'form_not_completed', label: 'Form Not Completed' },
  { value: 'test_results_received', label: 'Test Results Received' },
  { value: 'appointment_type_is', label: 'Appointment Type Is' },
  { value: 'has_active_medication', label: 'Has Active Medication' },
];

export const CONDITION_OPERATORS = [
  { value: 'and', label: 'ALL must match (AND)' },
  { value: 'or', label: 'ANY must match (OR)' },
];

export const NUMERIC_OPERATORS = [
  { value: 'gte', label: '≥' },
  { value: 'lte', label: '≤' },
  { value: 'eq', label: '=' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
];

export const NOTIFICATION_CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'push', label: 'Push Notification' },
];

export const NOTIFICATION_RECIPIENTS = [
  { value: 'patient',                  label: 'Patient (plan recipient)' },
  // { value: 'practitioner_plan',        label: 'Practitioner (assigned to plan)' },
  { value: 'practitioner_appointment', label: 'Practitioner (next appointment)' },
  { value: 'practitioner_specific',    label: 'Specific practitioner' },
];

export const TEST_PRIORITIES = [
  { value: 'routine', label: 'Routine' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'stat', label: 'STAT' },
];

// ── Practitioner-input types ────────────────────────────────────────────

/** A single field that a practitioner must fill in before an action stage executes. */
export interface PractitionerInputField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'number';
  required: boolean;
  default: string;
}

/**
 * One entry in the input_schema stored in a stage log's outcome_data when
 * awaiting_action === 'practitioner_input'.  Maps to one action in the stage.
 */
export interface PractitionerInputSchemaItem {
  action_index: number;
  action_type: string;
  title: string;
  fields: PractitionerInputField[];
}

// ── Peptide Dashboard types ─────────────────────────────────────────────────

export interface PendingPractitionerTask {
  plan_id: number;
  patient_id: UserUuid;
  stage_log_id: number;
  plan_name: string | null;
  task_type: string | null;
  task_title: string | null;
  form_id: number | null;
  form_name: string | null;
  entered_at: string;
}

export interface PatientFormWaiting {
  plan_id: number;
  patient_id: UserUuid;
  stage_log_id: number;
  plan_name: string | null;
  form_id: number | null;
  form_name: string | null;
  item_key: string | null;
  entered_at: string;
}

export interface PendingDashboardStats {
  active_plans: number;
  pending_practitioner_tasks: number;
  patient_forms_waiting: number;
  completed_plans: number;
}

export interface ActivePlanStageOverview {
  snapshot_id: number;
  stage_key: string;
  stage_order: number;
  title: string | null;
  stage_type: string;
  /** 'not_started' when no stage log exists yet */
  status: StageLogStatus | 'not_started';
  entered_at: string | null;
  section_key?: string | null;
  stage_log_id?: number | null;
  configuration?: Record<string, any>;
  /** Loop iteration this overview entry belongs to (0-based). Optional until backend B3 ships. */
  loop_iteration?: number;
  /** Per-attempt key (backend sends the snapshot id). Optional until backend B3 ships. */
  attempt_key?: number;
}

export interface ActivePlanOverview {
  plan_id: number;
  patient_id: UserUuid;
  plan_name: string | null;
  status?: PlanStatus;
  assigned_at: string | null;
  stages: ActivePlanStageOverview[];
  current_section?: TreatmentPlanSectionInfo | null;
  sections?: TreatmentPlanSectionInfo[];
  template_id?: number;
  has_comprehensive_analysis?: boolean;
}

export interface PendingDashboardData {
  practitioner_tasks: PendingPractitionerTask[];
  patient_forms_waiting: PatientFormWaiting[];
  active_plans: ActivePlanOverview[];
  stats: PendingDashboardStats;
}

// ── Icon mapping for visual representation ──────────────────────────────────

/** Maps stage types to their display icons. Icons are imported in the component. */
export const STAGE_TYPE_ICON_MAP: Record<StageType, string> = {
  trigger: 'RocketOutlined',
  action: 'ThunderboltOutlined',
  condition: 'BranchesOutlined',
  time_condition: 'HourglassOutlined',
  watcher: 'EyeOutlined',
  manual: 'UserOutlined',
  sub_plan_selection: 'ApartmentOutlined',
  appointment: 'CalendarOutlined',
  practitioner_task: 'CheckSquareOutlined',
  patient_analysis: 'RobotOutlined',
  practitioner_option: 'ForkOutlined',
  comprehensive_analysis: 'ExperimentOutlined',
  admin_review: 'SafetyCertificateOutlined',
};

/** Maps practitioner task types to their display icons */
export const PRACTITIONER_TASK_ICON_MAP: Record<PractitionerTaskType, string> = {
  upload_resource: 'UploadOutlined',
  complete_practitioner_form: 'FormOutlined',
  create_test_order: 'ExperimentOutlined',
  document_medical_history: 'HistoryOutlined',
  record_vital_signs: 'HeartOutlined',
  complete_referral: 'CopyOutlined',
  prescribe_medication: 'MedicineBoxOutlined',
  select_dispensing_pharmacy: 'BankOutlined',
};
